import { useEffect, useState } from "react";
import { Cast } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";

const MIME = (url) => (/\.(mp3|m4a|aac)(\?|$)/i.test(url) ? "audio/mpeg" : "video/mp4");

function castContext() {
  const cf = window.cast?.framework;
  if (!cf) return null;
  const ctx = cf.CastContext.getInstance();
  if (!ctx.getCastState) return null;
  if (!window.__castInit || window.__castInitId !== (window.__castAppId || "default")) {
    ctx.setOptions({ receiverApplicationId: window.__castAppId || window.chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID, autoJoinPolicy: window.chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED });
    window.__castInit = true;
    window.__castInitId = window.__castAppId || "default";
  }
  return ctx;
}

export function useCast() {
  const [state, setState] = useState(() => (window.__castReady ? "idle" : "unavailable"));
  useEffect(() => {
    const sync = () => { const ctx = castContext(); if (!ctx) return; setState(ctx.getCastState() === "NO_DEVICES_AVAILABLE" ? "nodevices" : ctx.getCurrentSession() ? "connected" : "idle"); };
    const onReady = () => { sync(); castContext()?.addEventListener(window.cast.framework.CastContextEventType.CAST_STATE_CHANGED, sync); };
    if (window.__castReady) onReady(); else window.addEventListener("castready", onReady);
    window.addEventListener("castappid", sync);
    return () => { window.removeEventListener("castready", onReady); window.removeEventListener("castappid", sync); };
  }, []);
  const cast = async (url, title, poster) => {
    const ctx = castContext();
    if (!ctx) return toast.error("Chromecast is only available in Chrome on desktop or Android");
    try {
      await ctx.requestSession();
      const info = new window.chrome.cast.media.MediaInfo(url, MIME(url));
      info.metadata = new window.chrome.cast.media.GenericMediaMetadata();
      info.metadata.title = title;
      if (poster) info.metadata.images = [{ url: poster }];
      await ctx.getCurrentSession().loadMedia(new window.chrome.cast.media.LoadRequest(info));
      toast.success(`Casting "${title}" to ${ctx.getCurrentSession().getCastDevice().friendlyName}`);
    } catch (e) {
      if (e !== "cancel") toast.error(e === "receiver_unavailable" ? "No Chromecast devices found on your network" : "Could not start casting");
    }
  };
  const stop = () => castContext()?.endCurrentSession(true);
  return { state, cast, stop };
}

export default function CastButton({ url, title, poster, size = "sm", className = "", testId = "cast-button" }) {
  const { state, cast, stop } = useCast();
  if (state === "unavailable" || !url) return null;
  const connected = state === "connected";
  return (
    <Button size={size} variant="outline" onClick={() => (connected ? stop() : cast(url, title, poster))} data-testid={testId} title={state === "nodevices" ? "No Chromecast found on this network" : connected ? "Stop casting" : "Cast to TV"}
      className={`bg-transparent border-slate-700 ${connected ? "text-cyan-300 border-cyan-400/60" : ""} ${className}`}>
      <Cast className="w-3.5 h-3.5 mr-1.5" />{connected ? "Casting" : "Cast to TV"}
    </Button>
  );
}
